import axios, { AxiosHeaders } from 'axios';
import { HttpError } from './httpError.js';
function isNodeReadableStream(data) {
    return (typeof data === 'object' &&
        data !== null &&
        typeof data.pipe === 'function' &&
        typeof data.toArray === 'function');
}
const CORRELATION_ID = 'X-Correlation-Id';
const CALLER_ID = 'X-Caller-Id';
const TEST_STRATEGY = 'X-Test-Strategy';
const LOCALE = 'Idf-Locale';
export function isAxiosClientError(error) {
    if (!error) {
        return false;
    }
    return error?.isAxiosError === true;
}
export function createAxiosClient(apiHost, options) {
    const client = axios.create({
        baseURL: `${apiHost}`,
        timeout: options.timeout,
        headers: {
            Accept: options.accept ?? 'application/json',
        },
    });
    async function accessTokenInterceptor() {
        if (options.accessTokenSource !== undefined) {
            const accessToken = await options.accessTokenSource();
            if (accessToken !== null) {
                return new AxiosHeaders({ Authorization: `Bearer ${accessToken}` });
            }
        }
        return new AxiosHeaders();
    }
    async function correlationIdInterceptor() {
        if (options.correlationIdSource !== undefined) {
            const correlationId = await options.correlationIdSource();
            if (correlationId !== null) {
                return new AxiosHeaders({ [CORRELATION_ID]: correlationId });
            }
        }
        return new AxiosHeaders();
    }
    function callerIdInterceptor() {
        const { callerId } = options;
        if (callerId) {
            return new AxiosHeaders({ [CALLER_ID]: callerId });
        }
        return new AxiosHeaders();
    }
    function localeSourceInterceptor() {
        const { localeSource } = options;
        if (localeSource) {
            return new AxiosHeaders({ [LOCALE]: localeSource() });
        }
        return new AxiosHeaders();
    }
    async function testStrategyInterceptor() {
        const { testStrategySource } = options;
        if (testStrategySource) {
            const testStrategy = await testStrategySource();
            if (testStrategy) {
                return new AxiosHeaders({ [TEST_STRATEGY]: testStrategy });
            }
        }
        return new AxiosHeaders();
    }
    function customHeadersInterceptor() {
        const { customHeaders } = options;
        if (customHeaders) {
            return new AxiosHeaders(customHeaders);
        }
        return new AxiosHeaders();
    }
    client.interceptors.request.use(async (config) => {
        const tokenHeader = await accessTokenInterceptor();
        const correlationIdHeader = await correlationIdInterceptor();
        const callerIdHeader = callerIdInterceptor();
        const localeHeader = localeSourceInterceptor();
        const testStrategyHeader = await testStrategyInterceptor();
        const customHeaders = customHeadersInterceptor();
        const headers = config.headers.concat(tokenHeader, correlationIdHeader, callerIdHeader, localeHeader, testStrategyHeader, customHeaders);
        return {
            ...config,
            headers,
        };
    });
    const MAINTENANCE_STATUS_CODE = 580;
    if (typeof window !== 'undefined') {
        client.interceptors.response.use(undefined, (error) => {
            if (isAxiosClientError(error) && error.response?.status === MAINTENANCE_STATUS_CODE) {
                window.location.href = '/maintenance';
                return new Promise(() => { });
            }
            return Promise.reject(error);
        });
    }
    client.interceptors.response.use(undefined, (error) => {
        if (isAxiosClientError(error) && options.useHttpError) {
            const responseData = error.response?.data;
            const unStreamedData = isNodeReadableStream(responseData)
                ? responseData.toArray()
                : Promise.resolve(responseData);
            return unStreamedData.then((data) => {
                throw new HttpError(error, data);
            });
        }
        return Promise.reject(error);
    });
    return client;
}
